import { Action, Dispatch } from 'redux';
import _ from 'lodash';
import { AssetHit } from '../api/vault/searchEngine';
import { IAppState } from './reducers';
import { bulkUpdateAsset } from '../api/vault/assets/api';
import { updateSearchHits } from './search';
import { selectUserRole } from './user/userOrgRole';
import { selectableProfileNames, unshownSecurityProfileIds } from '../utils/assetDetails/securityProfileUpdate';

export interface IState {
  isBulkEditButtonClicked: boolean;
  selectedAssets: AssetHit[];
  editableMetaTags: any;
  editedFields: any;
  isUpdatingInProgress: boolean;
}

export enum ActionType {
  BulkEditButtonClicked = 'BulkUpdateAssets.BulkUpdateButtonClicked',
  BulkEditResetState = 'BulkUpdateAssets.BulkUpdateResetState',
  BulkEditUpdateEditableMetaTags = 'BulkUpdateAssets.BulkEditUpdateEditableMetaTags',
  BulkEditResetEditedFields = 'BulkUpdateAssets.BulkEditResetEditedFields',
  BulkEditIsUpdatingInProgress = 'BulkUpdateAssets.BulkEditIsUpdatingInProgress',
  UpdateEditedFields = 'BulkUpdateAssets.UpdateEditedFields',
}
export interface IBulkEditAssets extends Action<ActionType> {
  payload?: any;
}

export const INITIAL_STATE: IState = {
  isBulkEditButtonClicked: false,
  selectedAssets: [],
  editableMetaTags: [],
  editedFields: {},
  isUpdatingInProgress: false,
};

export default function reducer(state: IState = INITIAL_STATE, action: IBulkEditAssets): IState {
  switch (action.type) {
    case ActionType.BulkEditButtonClicked:
      return {
        ...state,
        ...action.payload,
      };
    case ActionType.BulkEditUpdateEditableMetaTags:
      return {
        ...state,
        editableMetaTags: action.payload,
      };
    case ActionType.UpdateEditedFields:
      return {
        ...state,
        editedFields: {
          ...state.editedFields,
          ...action.payload,
        },
      };
    case ActionType.BulkEditResetEditedFields:
      return {
        ...state,
        editedFields: {},
      };
    case ActionType.BulkEditIsUpdatingInProgress:
      return {
        ...state,
        isUpdatingInProgress: action.payload,
      };
    case ActionType.BulkEditResetState:
      return INITIAL_STATE;
    default:
      return state;
  }
}

export const updateBulkEditEditableMetaData = (editableMetaData: any) => async (dispatch: Dispatch<IBulkEditAssets>, getState: () => IAppState) => {
  dispatch({
    type: ActionType.BulkEditUpdateEditableMetaTags,
    payload: editableMetaData || [],
  });
};

export const resetEditedFields = () => async (dispatch: Dispatch<IBulkEditAssets>) => {
  dispatch({
    type: ActionType.BulkEditResetEditedFields,
  });
};

export const toggleBulkEditAssets = (isBulkEditButtonClicked: boolean, selectedAssets: AssetHit[]) => async (dispatch: Dispatch<any>, getState: () => IAppState) => {
  // Reset the edited fields whenever this dialog is opened or closed
  dispatch(resetEditedFields());

  if (isBulkEditButtonClicked) {
    // Load in the editable assets
    const { metatags } = getState();
    const editableMetaTags = metatags && metatags.metaTags ? _.cloneDeep(metatags.metaTags) : [];
    dispatch({
      type: ActionType.BulkEditUpdateEditableMetaTags,
      payload: editableMetaTags || [],
    });

    // Load in the selected assets
    dispatch({
      payload: {
        isBulkEditButtonClicked,
        selectedAssets,
      },
      type: ActionType.BulkEditButtonClicked,
    });
  } else { // reset state
    dispatch({
      type: ActionType.BulkEditResetState,
    });
  }
};

export function setIsUpdatingInProgress(isUpdatingInProgress: boolean) {
  return {
    type: ActionType.BulkEditIsUpdatingInProgress,
    payload: isUpdatingInProgress,
  };
}

export const addAssetUpdates = (update: {}) => ({
  type: ActionType.UpdateEditedFields,
  payload: update,
});

export const bulkUpdate = (callback: () => void) => async (dispatch: Dispatch<any>, getState: () => IAppState) => {
  const state = getState();
  const userRole = selectUserRole(state);
  const { bulkEditAssetsDialog: { editedFields, selectedAssets } } = state;
  dispatch(setIsUpdatingInProgress(true));
  if (!_.isEmpty(editedFields)) {
    // Add user info
    editedFields.modifiedBy = userRole.userInfo.displayName;
    editedFields.modifiedDate = new Date().toISOString();

    // VV-315: Security replaces each asset's list with the profiles picked, which can only be
    // ones the field shows. Each asset keeps its own profiles that the field can't show - the
    // Film Room group, hidden from anyone but a super admin - or bulk editing an owner's Film
    // Room film would untag it, with no way to put the tag back.
    const profiles = state.securityProfiles?.idValueMapping ?? [];
    const shownNames = selectableProfileNames(state.securityProfiles?.productOptions);

    const assetsToUpdate: Array<any> = [];
    selectedAssets.forEach((asset) => {
      const clonedFields = _.cloneDeep(editedFields);
      clonedFields._id = asset._id;
      if (Array.isArray(clonedFields.securityProfiles)) {
        clonedFields.securityProfiles = _.uniq([
          ...clonedFields.securityProfiles,
          ...unshownSecurityProfileIds(asset._source?.securityProfiles, profiles, shownNames),
        ]);
      }
      assetsToUpdate.push(clonedFields);
    });

    try {
      const response = await bulkUpdateAsset(assetsToUpdate);
      if (response && response.status === 204) {
        updateSearchHits(assetsToUpdate)(dispatch, getState);
      }
    } catch (error) {
      console.log(error);
    }
  }
  callback();
  // Close dialog and reset some state
  dispatch(toggleBulkEditAssets(false, []));
  dispatch(setIsUpdatingInProgress(false));
};
